import * as React from 'react';
import classNames from 'classnames';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {FocusTrap} from 'components/FocusTrap';
import {Icon, NOOP} from 'components/UIKit';
import * as locales from '../../locales';
import style from './NavSearch.scss';

interface NavSearchInputProps {
  value: string;
  action: string;
  onChange: React.ChangeEventHandler;
  onCancel?: React.MouseEventHandler;
  className?: string;
  showMobile: boolean;
  placeholder?: string;
}

interface NavSearchInputState {
  focus: boolean;
}

/**
 * Excepting on mobile this component is an input with an inline search icon that will redirect to the provided search
 * endpoint on submission.
 *
 * On mobile this component will be position fixed to cover the header and shows a cancel icon. The component then must
 * be manually hidden and shown through a controlled prop `showMobile` and cancel event `onCancel`.
 */
class NavSearchInputComponent extends React.PureComponent<
  NavSearchInputProps & WithI18nProps,
  NavSearchInputState
> {
  state: NavSearchInputState = {
    focus: false,
  };

  /**
   * Manually keep track of focus state in order to alter icon color without hacky sibling selectors across components
   */
  handleBlur = () => this.setState({focus: false});
  handleFocus = () => this.setState({focus: true});

  render() {
    const {focus} = this.state;
    const {
      i18n,
      onChange,
      onCancel = NOOP,
      action,
      showMobile,
      className,
      value,
      placeholder = '',
    } = this.props;

    return (
      <FocusTrap active={showMobile} focusOnActive>
        {(focusRef) => (
          <form
            ref={focusRef}
            action={action}
            method="get"
            className={classNames(style.form, className, {
              [style.focus]: focus,
              [style.show]: showMobile,
            })}
          >
            <input
              id="search"
              type="search"
              name="search"
              value={value}
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
              onChange={onChange}
              required
              aria-label={placeholder}
              placeholder={placeholder}
              className={style.input}
            />
            <Icon
              icon="search"
              size={20}
              className={style.inputIcon}
              asButton
              buttonType="submit"
              aria-label={i18n.translate('searchButton')}
            />
            <Icon
              icon="close"
              size={20}
              onClick={onCancel}
              className={style.closeIcon}
              aria-label={i18n.translate('cancelSearchButton')}
              asButton
            />
          </form>
        )}
      </FocusTrap>
    );
  }
}

export const NavSearchInput = withI18n({
  fallback: locales.en,
  id: 'Nav',
})(NavSearchInputComponent);
